import { VARIABLE_DATA_TYPE_OPTIONS } from "@/constants/rule-variables";

export interface DataFieldTypeRef {
	id: number;
	name: string;
}

export interface ScreenLocationRef {
	name: string;
}

export interface ScreenFieldRef {
	name: string;
	dataFieldType?: DataFieldTypeRef;
	location?: ScreenLocationRef;
}

export interface FormulaVariableRef {
	id?: number;
	name: string;
	dataType?: DataFieldTypeRef;
}

export interface SelectedFieldMeta {
	label?: string;
	dataType?: string;
	locationName?: string;
	fieldName?: string;
	isCrossLocation?: boolean;
}

export function formatCrossLocationFieldLabel(fieldName: string, locationName?: string): string {
	const plain = fieldName.trim();
	if (!plain) return plain;
	const loc = locationName?.trim();
	if (!loc) return plain;
	return `${loc}.${plain}`;
}

export function extractPlainFieldNameFromQualifiedLabel(
	label: string,
	locationName?: string,
): string {
	const trimmed = label.trim();
	if (!trimmed) return trimmed;
	const loc = locationName?.trim();
	if (loc) {
		const prefix = `${loc}.`;
		if (trimmed.startsWith(prefix)) {
			return trimmed.slice(prefix.length);
		}
	}
	const dotIndex = trimmed.lastIndexOf(".");
	if (dotIndex >= 0) {
		return trimmed.slice(dotIndex + 1);
	}
	return trimmed;
}

export function isCrossLocationScreenField(
	fieldId: string | undefined,
	screenFieldRef: ScreenFieldRef | undefined,
	currentScreenFieldIds: ReadonlySet<string>,
	ruleLocationName?: string,
): boolean {
	if (fieldId && currentScreenFieldIds.has(fieldId)) {
		return false;
	}
	const refLocation = screenFieldRef?.location?.name?.trim();
	if (refLocation && ruleLocationName?.trim()) {
		return refLocation !== ruleLocationName.trim();
	}
	if (fieldId && !currentScreenFieldIds.has(fieldId)) {
		return true;
	}
	return Boolean(refLocation);
}

export function resolveScreenFieldDisplayLabel(
	label: string,
	fieldId: string | undefined,
	currentScreenFieldIds: ReadonlySet<string>,
	options?: {
		screenFieldRef?: ScreenFieldRef;
		ruleLocationName?: string;
		locationName?: string;
	},
): string {
	const { screenFieldRef, ruleLocationName, locationName } = options ?? {};
	const ref = screenFieldRef;
	const locName = locationName?.trim() || ref?.location?.name?.trim();
	const plainName =
		ref?.name?.trim() ||
		extractPlainFieldNameFromQualifiedLabel(label, locName);

	const crossLocation = isCrossLocationScreenField(
		fieldId,
		ref ?? (locName ? { name: plainName, location: { name: locName } } : { name: plainName }),
		currentScreenFieldIds,
		ruleLocationName,
	);

	if (!crossLocation) {
		if (fieldId && currentScreenFieldIds.has(fieldId)) {
			return label;
		}
		return plainName || label;
	}

	if (locName) {
		return formatCrossLocationFieldLabel(plainName, locName);
	}

	if (label.includes(".") && fieldId && !currentScreenFieldIds.has(fieldId)) {
		return label;
	}

	return plainName || label;
}

export function mapApiDataFieldType(
	raw?: { id?: number; name?: string } | null,
): DataFieldTypeRef | undefined {
	if (raw == null || raw.id == null || typeof raw.name !== "string" || !raw.name.trim()) {
		return undefined;
	}
	return { id: Number(raw.id), name: raw.name.trim() };
}

export function mapApiScreenFieldRef(
	raw?: {
		name?: string;
		dataFieldType?: { id?: number; name?: string } | null;
		location?: { name?: string } | null;
	} | null,
): ScreenFieldRef | undefined {
	if (raw == null || typeof raw.name !== "string" || !raw.name.trim()) {
		return undefined;
	}
	const dataFieldType = mapApiDataFieldType(raw.dataFieldType);
	const locationName =
		typeof raw.location?.name === "string" && raw.location.name.trim()
			? raw.location.name.trim()
			: undefined;
	const base: ScreenFieldRef = {
		name: raw.name.trim(),
		...(locationName ? { location: { name: locationName } } : {}),
	};
	return dataFieldType ? { ...base, dataFieldType } : base;
}

export function dataFieldTypeFromPickerName(name?: string): DataFieldTypeRef | undefined {
	if (typeof name !== "string" || !name.trim()) return undefined;
	const normalized = name.trim().toLowerCase();
	const match = VARIABLE_DATA_TYPE_OPTIONS.find(
		(o) =>
			o.label.toLowerCase() === normalized ||
			o.value.toLowerCase() === normalized,
	);
	if (!match) return undefined;
	return { id: match.id, name: match.label };
}

export function buildScreenFieldRef(
	name?: string,
	pickerDataTypeName?: string,
	existing?: ScreenFieldRef,
	locationName?: string,
): ScreenFieldRef | undefined {
	if (typeof name !== "string" || !name.trim()) return undefined;
	const resolvedLocationName = locationName?.trim() || existing?.location?.name?.trim();
	const plainName = extractPlainFieldNameFromQualifiedLabel(name, resolvedLocationName);
	const dataFieldType =
		dataFieldTypeFromPickerName(pickerDataTypeName) ??
		existing?.dataFieldType ??
		undefined;
	const location = resolvedLocationName ? { name: resolvedLocationName } : existing?.location;
	const base: ScreenFieldRef = {
		name: plainName,
		...(location ? { location } : {}),
	};
	return dataFieldType ? { ...base, dataFieldType } : base;
}

export function mapApiFormulaVariableRef(
	raw?: {
		id?: number;
		name?: string;
		dataType?: { id?: number; name?: string } | null;
	} | null,
): FormulaVariableRef | undefined {
	if (raw == null || typeof raw.name !== "string" || !raw.name.trim()) {
		return undefined;
	}
	const dataFieldType = mapApiDataFieldType(raw.dataType);
	const base: FormulaVariableRef = {
		name: raw.name.trim(),
		...(raw.id != null ? { id: Number(raw.id) } : {}),
	};
	return dataFieldType ? { ...base, dataType: dataFieldType } : base;
}

export function buildFormulaVariableRef(
	name?: string,
	pickerDataTypeName?: string,
	existing?: FormulaVariableRef,
	id?: string | number,
): FormulaVariableRef | undefined {
	if (typeof name !== "string" || !name.trim()) return undefined;
	const dataFieldType =
		dataFieldTypeFromPickerName(pickerDataTypeName) ??
		existing?.dataType ??
		undefined;
	const parsedId = id != null ? Number(id) : existing?.id;
	const base: FormulaVariableRef = {
		name: name.trim(),
		...(parsedId != null && !Number.isNaN(parsedId) ? { id: parsedId } : {}),
	};
	return dataFieldType ? { ...base, dataType: dataFieldType } : base;
}

export function selectedFieldMetaFromFormulaVariable(
	ref?: FormulaVariableRef,
): SelectedFieldMeta | undefined {
	if (ref == null || typeof ref.name !== "string" || !ref.name.trim()) {
		return undefined;
	}
	return {
		label: ref.name.trim(),
		dataType: ref.dataType?.name,
	};
}

export function selectedFieldMetaFromScreenField(
	field?: ScreenFieldRef | { name?: string; dataFieldType?: DataFieldTypeRef; location?: ScreenLocationRef },
	fieldId?: string,
	options?: {
		currentScreenFieldIds?: ReadonlySet<string>;
		ruleLocationName?: string;
	},
): SelectedFieldMeta | undefined {
	if (field == null || typeof field.name !== "string" || !field.name.trim()) {
		return undefined;
	}
	const ref = field as ScreenFieldRef;
	const currentScreenFieldIds = options?.currentScreenFieldIds;
	const isCrossLocation = currentScreenFieldIds
		? isCrossLocationScreenField(fieldId, ref, currentScreenFieldIds, options?.ruleLocationName)
		: Boolean(ref.location?.name);

	const label = currentScreenFieldIds
		? resolveScreenFieldDisplayLabel(ref.name, fieldId, currentScreenFieldIds, {
				screenFieldRef: ref,
				ruleLocationName: options?.ruleLocationName,
			})
		: ref.location?.name
			? formatCrossLocationFieldLabel(ref.name, ref.location.name)
			: ref.name.trim();

	return {
		label,
		dataType: field.dataFieldType?.name,
		locationName: ref.location?.name,
		fieldName: ref.name.trim(),
		isCrossLocation,
	};
}

/** Aliases for matching `{token}` in formulas/reactions (qualified + legacy plain). */
export function screenFieldTokenAliases(label: string): string[] {
	const trimmed = label.trim();
	if (!trimmed) return [];
	const aliases = new Set<string>([trimmed]);
	const dotIndex = trimmed.lastIndexOf(".");
	if (dotIndex >= 0) {
		aliases.add(trimmed.slice(dotIndex + 1));
	}
	return [...aliases];
}

/**
 * True when `{braceInner}` in the editor refers to a screen field with `label`.
 * Handles qualified `{Location.Field}` tokens when lookup still has plain `Field`.
 */
export function screenFieldBraceInnerMatchesLabel(braceInner: string, label: string): boolean {
	const trimmedInner = braceInner.trim();
	const trimmedLabel = label.trim();
	if (!trimmedInner || !trimmedLabel) {
		return false;
	}
	if (screenFieldTokenAliases(trimmedLabel).some((alias) => alias === trimmedInner)) {
		return true;
	}
	if (!trimmedLabel.includes(".") && trimmedInner.includes(".")) {
		return (
			extractPlainFieldNameFromQualifiedLabel(trimmedInner) ===
			extractPlainFieldNameFromQualifiedLabel(trimmedLabel)
		);
	}
	return false;
}
